'use client'

import React from 'react'
import { Container, Flex, FlexItem } from '@quark/layout'
import { Carousel } from '@quark/carousel'
import { Text } from '@quark/typography'
import { Avatar } from '@quark/avatar'
import { VideoPlayer } from '@quark/video'
import { Reveal } from '@quark/animation'
import RatingStars from '@components/CustomerStory/RatingStars/RatingStars'
import { BrandVars } from '@quark/theme'
import { TrustProps } from '@interfaces/home'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { Button } from '@quark/button'
import styles from './trust.module.scss'

const getCarouselContent = stories =>
  stories?.map((story, index) => ({
    content: (
      <Container
        key={index}
        textAlign="center"
        px={{ sm: 'spacing-40', md: 'spacing-112', lg: 'spacing-80' }}
        paddingTop={{ sm: 'spacing-32', md: 'spacing-32', lg: 'spacing-0' }}>
        <Flex gap="spacing-32" flexDirection="column">
          <Flex alignItems="center" gap="spacing-16" flexDirection="column">
            <RatingStars rating={story?.rating} total={5} />
            <Text as="div" fontStyle="um_text-base/normal" color="base/primary">
              {story?.description}
            </Text>
          </Flex>
          <Flex
            flexDirection="row"
            gap={{ sm: 'spacing-8', md: 'spacing-16', lg: 'spacing-16' }}
            size="fullWidth"
            alignItems="center"
            justifyContent="center">
            <Avatar size={{ sm: 'md', md: 'md', lg: 'md' }} type="circle" backgroundColor="gray/200">
              <Text fontStyle="um_text-xl/normal" color="base/primary">
                {story?.customerName.charAt(0).toUpperCase()}
              </Text>
            </Avatar>
            <Flex flexDirection="column">
              <Text textAlign="left" fontStyle="um_text-base/normal" color="base/primary">
                {story?.customerName}
              </Text>
              <Text textAlign="left" fontStyle="um_text-base/normal" color="base/secondary">
                {story?.customerCompany}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    ),
  }))

// const isMobile = false
const Trust = React.memo((data: TrustProps) => {
  const handleVideoClick = async (eventProperties: { eventName?: string; customProperties?: Record<string, any> }) => {
    await trackEvent(eventProperties?.eventName ?? 'tab_video_interaction', {
      page_type: 'acko_home_page',
      ...eventProperties?.customProperties,
    })
  }

  const handleViewMoreClick = async (eventProperties: { eventName?: string; customProperties?: Record<string, any> }) => {
    await trackEvent(eventProperties?.eventName ?? 'tap_btn_interaction', {
      page_type: 'acko_home_page',
      ...eventProperties?.customProperties,
    })
    return window.open(data?.carouselCardData?.viewMore?.url, '_blank')
  }

  return (
    <Container
      id={NewHomePageComponent.Trust}
      backgroundColor="base/overlay"
      type="fluid"
      borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}>
      <Container
        type="root"
        px={{ sm: 'spacing-16', md: 'spacing-32', lg: 'spacing-80' }}
        py={{ lg: 'spacing-80', md: 'spacing-56', sm: 'spacing-40' }}>
        <Flex gap={{ lg: 'spacing-64', md: 'spacing-40', sm: 'spacing-32' }} flexDirection="column">
          <Flex gap={{ lg: 'spacing-64', md: 'spacing-40', sm: 'spacing-32' }} flexDirection="column">
            <Reveal as="div" animationProps={{ threshold: 0.1, animationDelayMs: 500 }}>
              <Flex
                justifyContent="center"
                alignItems="center"
                gap={{ lg: 'spacing-32', md: 'spacing-16', sm: 'spacing-20' }}
                flexDirection="column">
                <Text
                  as="span"
                  display={{ lg: 'block', md: 'block', sm: 'none' }}
                  fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-4xl/bold' }}
                  color="base/primary"
                  textAlign="center"
                  whiteSpace="pre">
                  {data?.heading}
                </Text>
                <Text
                  as="span"
                  display={{ lg: 'none', md: 'none', sm: 'block' }}
                  fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-5xl/bold', lg: 'um_text-4xl/bold' }}
                  color="base/primary"
                  textAlign="center"
                  whiteSpace="pre">
                  {data?.mobileHeading}
                </Text>
                <Flex gap={{ sm: 'spacing-0', md: 'spacing-80', lg: 'spacing-80' }} flexDirection="row">
                  {data?.subHeading?.items?.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <FlexItem alignSelf="stretch">
                        <Flex gap="spacing-8" flexDirection="column" alignItems="center" justifyContent="center">
                          <Text
                            as="span"
                            gradientColor="text"
                            bgColor="linear-gradient(180deg, var(--text-accent-textBrand-dark, #8B5CF6) 38.89%, var(--Surface-accent-Pink-pink-800, rgba(236, 72, 153, 0.72)) 151.85%)"
                            fontStyle={{ sm: 'um_text-3xl/bold', md: 'um_text-5xl/bold', lg: 'um_text-5xl/bold' }}
                            color="base/primary">
                            {item?.title}
                          </Text>
                          <Text
                            textAlign="center"
                            fontStyle={{ sm: 'um_text-sm/normal', md: 'um_text-lg/normal', lg: 'um_text-lg/normal' }}
                            color="base/primary">
                            {item?.subTitle}
                          </Text>
                        </Flex>
                      </FlexItem>
                      {(Array.isArray(data?.subHeading?.items) && idx !== (data?.subHeading?.items.length ?? 0) - 1) && (
                        <div
                          style={{
                            background: `linear-gradient(180deg, ${BrandVars.Surface['base/overlay']} 10%, ${BrandVars.Surface['base/secondary']} 50%, ${BrandVars.Surface['base/overlay']} 90%)`,
                            width: '1px',
                          }}
                        >
                          &nbsp;
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </Flex>
              </Flex>
            </Reveal>
            <Flex gap={{ lg: 'spacing-24', md: 'spacing-24', sm: 'spacing-16' }} flexDirection="column" >
              <Reveal as="div" animationProps={{ threshold: 0.1, animationDelayMs: 500 }}>
                <Flex
                  justifyContent="center"
                  alignItems={{ sm: 'center', md: 'stretch', lg: 'stretch' }}
                  flexDirection={{ sm: 'column', md: 'column', lg: 'row' }}
                  gap={{ lg: 'spacing-24', md: 'spacing-24', sm: 'spacing-16' }}
                  size="fullWidth">
                  <Container
                    minWidth={0}
                    backgroundColor="base/elevated"
                    textAlign="center"
                    borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
                    paddingTop={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-20' }}
                    paddingLeft={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-20' }}
                    paddingRight={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-20' }}
                    overflow="hidden">
                    <Flex alignItems="center" justifyItems="center" gap="spacing-32" flexDirection="column">
                      <Text
                        fontStyle={{ sm: 'um_text-xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-3xl/bold' }}
                        color="base/primary">
                        {data?.videoCardData?.title}
                      </Text>
                      <div className={styles.mobile_frame}>
                        <video src={data?.videoCardData?.videoSrc} autoPlay muted loop playsInline />
                      </div>
                    </Flex>
                  </Container>
                  <Container
                    minWidth={0}
                    backgroundColor="base/elevated"
                    borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
                    padding={{ sm: 'spacing-20', md: 'spacing-32', lg: 'spacing-32' }}>
                    <Flex size='fullHeight' flexDirection="column" alignItems="center" gap={{ sm: 'spacing-20', md: 'spacing-144', lg: 'spacing-112' }}>
                      <Text
                        textAlign="center"
                        fontStyle={{ sm: 'um_text-xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-3xl/bold' }}
                        color="base/primary"
                        as="div">
                        {data?.carouselCardData?.title}
                      </Text>
                      <Flex
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={{ sm: 'spacing-20', md: 'spacing-144', lg: 'spacing-64' }}
                        size="fullSize">
                        <Carousel content={getCarouselContent(data?.carouselCardData?.stories ?? [])} showDots={false} />
                        {data?.carouselCardData?.viewMore && <Button
                          type='solid'
                          size={{ sm: 'block', md: 'md', lg: 'md' }}
                          onClick={() => {
                            void handleViewMoreClick(data?.carouselCardData?.viewMore?.event || {})
                          }}
                        >
                          <Text fontStyle="um_text-base/medium" color='base/inverted primary'> {data?.carouselCardData?.viewMore?.title} </Text>
                        </Button>}
                      </Flex>
                    </Flex>
                  </Container>
                </Flex>
              </Reveal>
              <Reveal as="div" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
                <Container
                  borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
                  overflow="hidden"
                  type="fluid">
                  <VideoPlayer
                    heading={
                      <>
                        <Text
                          fontStyle={{ sm: 'um_text-base/medium', md: 'um_text-2xl/medium', lg: 'um_text-3xl/medium' }}
                          color="static/Primary-onDark"
                          as="div">
                          &quot; 100% claim settlement is
                        </Text>
                        <Text
                          fontStyle={{ sm: 'um_text-base/medium', md: 'um_text-2xl/medium', lg: 'um_text-3xl/medium' }}
                          color="static/Primary-onDark"
                          as="div">
                          most important to us when it
                        </Text>
                        <Text
                          fontStyle={{ sm: 'um_text-base/medium', md: 'um_text-2xl/medium', lg: 'um_text-3xl/medium' }}
                          color="static/Primary-onDark"
                          as="div">
                          comes to building trust &quot;
                        </Text>
                      </>
                    }
                    onClick={() => {
                      void handleVideoClick(data?.videoEventProps || {})
                    }}
                    thumbnail={data?.videoPlayerData?.videoThumbnail ?? ''}
                    title="Varun Dua"
                    authorData={{
                      authorName: 'Varun Dua',
                      authorDesignation: 'CEO, ACKO',
                    }}
                    videoSrcSet={{
                      lg: data?.videoPlayerData?.videoSrcSet?.lg ?? '',
                      md: data?.videoPlayerData?.videoSrcSet?.md ?? '',
                      sm: data?.videoPlayerData?.videoSrcSet?.sm ?? '',
                    }}
                  />
                </Container>
              </Reveal>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </Container>
  )
})

export default Trust
